/**
 * SMS Analytics Dashboard
 * Track SMS delivery, engagement, and campaign performance
 */

import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { AdminPageLayout, StatsGrid } from '@/components/layout/AdminPageLayout';
import { StatCard } from '@/components/layout/AdminStatCard';
import { Card } from '@/components/ui/card';
import { MessageSquare, CheckCircle, XCircle, TrendingUp } from 'lucide-react';

export default function SMSAnalytics() {
  const [stats, setStats] = useState({
    totalSent: 0,
    delivered: 0,
    failed: 0,
    optedOut: 0,
    deliveryRate: 0,
    avgCost: 0
  });
  const [recentMessages, setRecentMessages] = useState<any[]>([]);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    // Get last 30 days of messages
    const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();

    const { data: messages } = await supabase
      .from('message_outbox')
      .select('*')
      .gte('created_at', thirtyDaysAgo)
      .order('created_at', { ascending: false })
      .limit(50);

    if (messages) {
      const sent = messages.filter(m => m.status === 'sent').length;
      const failed = messages.filter(m => m.status === 'failed').length;

      setStats({
        totalSent: messages.length,
        delivered: sent,
        failed: failed,
        deliveryRate: messages.length > 0 ? (sent / messages.length * 100) : 0,
        optedOut: 0, // Would query sms_preferences
        avgCost: 0.0075 // Twilio avg cost per SMS
      });

      setRecentMessages(messages.slice(0, 20));
    }
  };

  return (
    <AdminPageLayout
      title="SMS Analytics"
      subtitle="Message delivery and engagement metrics"
    >
      {/* Key Metrics */}
      <StatsGrid cols={4}>
        <StatCard label="Total Sent" value={stats.totalSent} icon={MessageSquare} />
        <StatCard label="Delivered" value={stats.delivered} icon={CheckCircle} variant="success" />
        <StatCard label="Failed" value={stats.failed} icon={XCircle} variant="critical" />
        <StatCard 
          label="Delivery Rate" 
          value={`${stats.deliveryRate.toFixed(1)}%`} 
          icon={TrendingUp}
        />
      </StatsGrid>

      {/* Recent Messages */}
      <Card className="p-6">
        <h2 className="text-lg font-semibold mb-4">Recent Messages</h2>
        <div className="overflow-x-auto -mx-6">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Template</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sent</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {recentMessages.map((msg) => (
                <tr key={msg.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm">{msg.to_phone}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">{msg.template_key}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded ${
                      msg.status === 'sent' ? 'bg-green-100 text-green-800' :
                      msg.status === 'failed' ? 'bg-red-100 text-red-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {msg.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(msg.created_at).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </AdminPageLayout>
  );
}
